/**
 * Helpers for Supabase email-confirmation redirects (`/auth` with `type=signup`).
 * Capture tokens synchronously — Supabase clears the hash after detectSessionInUrl.
 */

export type SignupConfirmTokens = {
  access_token: string;
  refresh_token: string;
};

const EXPECTED_USER_STORAGE_KEY = 'bravos.signupConfirmUserId';

let capturedSignupConfirmTokens: SignupConfirmTokens | null = null;
let capturedSignupConfirmFlag = false;
let capturedSignupConfirmUserId: string | null = null;

function readStoredExpectedUserId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(EXPECTED_USER_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredExpectedUserId(userId: string | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (userId) sessionStorage.setItem(EXPECTED_USER_STORAGE_KEY, userId);
    else sessionStorage.removeItem(EXPECTED_USER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function hashAndQueryParams(urlLike?: string): { hashParams: URLSearchParams; queryParams: URLSearchParams } {
  if (typeof urlLike === 'string') {
    try {
      const u = new URL(urlLike, 'https://example.invalid');
      const hashRaw = u.hash.startsWith('#') ? u.hash.slice(1) : u.hash;
      return {
        hashParams: new URLSearchParams(hashRaw),
        queryParams: u.searchParams,
      };
    } catch {
      return { hashParams: new URLSearchParams(), queryParams: new URLSearchParams() };
    }
  }
  if (typeof window === 'undefined') {
    return { hashParams: new URLSearchParams(), queryParams: new URLSearchParams() };
  }
  const hashRaw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  return {
    hashParams: new URLSearchParams(hashRaw),
    queryParams: new URLSearchParams(window.location.search),
  };
}

/** Decode JWT payload `sub` (no signature verify — used only to match session user). */
export function userIdFromAccessToken(accessToken: string): string | null {
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === 'function'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('utf8');
    const payload = JSON.parse(json) as { sub?: unknown };
    return typeof payload.sub === 'string' && payload.sub ? payload.sub : null;
  } catch {
    return null;
  }
}

/** True when the auth redirect URL indicates a signup email confirmation. */
export function urlIndicatesSignupConfirmation(urlLike?: string): boolean {
  const { hashParams, queryParams } = hashAndQueryParams(urlLike);
  return queryParams.get('type') === 'signup' || hashParams.get('type') === 'signup';
}

/**
 * Access/refresh tokens from a signup confirmation link, if present.
 * Returns null when type is not signup or tokens are missing.
 */
export function parseSignupConfirmTokens(urlLike?: string): SignupConfirmTokens | null {
  const { hashParams, queryParams } = hashAndQueryParams(urlLike);
  const type = queryParams.get('type') || hashParams.get('type');
  if (type !== 'signup') return null;
  const access_token = hashParams.get('access_token') || queryParams.get('access_token');
  const refresh_token = hashParams.get('refresh_token') || queryParams.get('refresh_token');
  if (!access_token || !refresh_token) return null;
  return { access_token, refresh_token };
}

/**
 * Snapshot signup-confirm tokens from the current URL before `createClient`
 * (detectSessionInUrl clears the hash). Call once at app boot.
 */
export function captureSignupConfirmTokensFromLocation(): SignupConfirmTokens | null {
  if (urlIndicatesSignupConfirmation()) {
    capturedSignupConfirmFlag = true;
  }
  const tokens = parseSignupConfirmTokens();
  if (tokens) {
    capturedSignupConfirmTokens = tokens;
    const uid = userIdFromAccessToken(tokens.access_token);
    capturedSignupConfirmUserId = uid;
    if (uid) writeStoredExpectedUserId(uid);
  }
  return capturedSignupConfirmTokens;
}

/** True if this page load was a signup confirmation redirect (live URL or boot capture). */
export function wasSignupConfirmationRedirect(): boolean {
  return urlIndicatesSignupConfirmation() || capturedSignupConfirmFlag;
}

/** Tokens captured at boot, or a live URL parse if still present. */
export function getSignupConfirmTokens(): SignupConfirmTokens | null {
  return parseSignupConfirmTokens() || capturedSignupConfirmTokens;
}

/**
 * User id (`sub`) from the confirmation link. Survives token clear via sessionStorage
 * so the Auth confirm screen can refuse a mismatched prior session.
 */
export function getExpectedSignupConfirmUserId(): string | null {
  const tokens = getSignupConfirmTokens();
  if (tokens) {
    return userIdFromAccessToken(tokens.access_token)
      || capturedSignupConfirmUserId
      || readStoredExpectedUserId();
  }
  return capturedSignupConfirmUserId || readStoredExpectedUserId();
}

/** Drop in-memory boot snapshot (keeps sessionStorage expected user until {@link clearSignupConfirmExpectedUser}). */
export function clearCapturedSignupConfirmTokens(): void {
  capturedSignupConfirmTokens = null;
  capturedSignupConfirmFlag = false;
  capturedSignupConfirmUserId = null;
}

/** Clear expected-user gate (call when leaving the confirmation screen). */
export function clearSignupConfirmExpectedUser(): void {
  capturedSignupConfirmUserId = null;
  writeStoredExpectedUserId(null);
}
